import { useEffect } from 'react';
import { useAuth } from '../lib/auth';
import { catchUpRecurrence } from '../lib/recurrence';
import { todayInputValue } from '../lib/taskDates';
import { useDayBoundary } from '../lib/useDayBoundary';

/**
 * Tells the server what day it is, so a repeating task whose due date has
 * arrived brings on its next instance.
 *
 * The server runs nothing on a timer, and only the browser knows the reader's
 * local day -- the same split the dashboard's `dueTo` already makes. So the
 * moments that matter are the ones where somebody could see a stale list: the
 * app opening, the tab coming back, and midnight passing with it on screen.
 * `useDayBoundary` covers the last; the first two are below.
 *
 * Mounted beside the routes like ReminderScheduler, for the same reason: it has
 * to run whichever page is open. Renders nothing. Failures are swallowed --
 * the next return to the tab simply tries again, and nobody is waiting on it.
 */
export function RecurrenceCatchUp() {
  const { user } = useAuth();
  const signedIn = Boolean(user);

  useEffect(() => {
    if (!signedIn) return;

    const run = () => {
      if (document.visibilityState === 'hidden') return;
      catchUpRecurrence(todayInputValue()).catch(() => {});
    };

    run();
    window.addEventListener('focus', run);
    document.addEventListener('visibilitychange', run);
    return () => {
      window.removeEventListener('focus', run);
      document.removeEventListener('visibilitychange', run);
    };
  }, [signedIn]);

  useDayBoundary((today) => {
    if (signedIn) catchUpRecurrence(today).catch(() => {});
  });

  return null;
}
